import React, { useState } from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { MENTORING_OFFERS, MENTORING_TIERS } from "@/lib/data";
import { PublicAPI } from "@/lib/api";
import InvestmentSection from "@/components/Investment";

export default function Workshops() {
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        offer_type: "",
        current_status: "",
        goals: "",
        portfolio_url: "",
        why: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email) {
            toast.error("Please add your name and email.");
            return;
        }
        try {
            setSubmitting(true);
            const cleaned = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
            await PublicAPI.createMentoring(cleaned);
            setSubmitted(true);
            toast.success("Application received — you will hear back within 1–2 business days.");
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pt-24 sm:pt-28">
            <Seo {...SEO.mentoring} />
            <Section spacing="tight">
                <Kicker>Studio School · Mentoring</Kicker>
                <SerifTitle className="mt-3">Learn to make visuals that feel inevitable.</SerifTitle>
                <p className="mt-5 max-w-[64ch] text-sm leading-[1.85] text-muted-foreground">
                    A school inside the house. For photographers building taste, voice, and a more profitable business — and for founders elevating their personal brand. Private, considered, and tailored to your stage. Develop creative direction, posing, storytelling, premium positioning, pricing, and profitable creative offers.
                </p>
            </Section>

            <InvestmentSection
                kicker="Studio School · Investment"
                title="Four ways to work together."
                subtitle="A clear path whether you need quick clarity, a strategic audit, or sustained mentoring across creative direction and business."
                packages={MENTORING_TIERS}
                customNote="Mentoring engagements are tailored to your stage and goals. Pricing begins at the starting figure and is finalized after a short intake."
                columns={4}
                className="bg-background"
            />

            <Section spacing="normal" className="border-t border-border">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <Kicker>Apply</Kicker>
                        <SerifTitle className="mt-3">A small intake to begin.</SerifTitle>
                        <p className="mt-5 text-sm text-muted-foreground max-w-[44ch]">
                            Spots are limited. A short, considered application helps me understand where you are and where you want to go.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        {submitted ? (
                            <div data-testid="workshops-apply-success" className="border border-[hsl(var(--lux-brass))]/40 bg-[hsl(var(--lux-porcelain))] p-8 rounded-[2px]">
                                <div className="kicker">Thank you</div>
                                <h3 className="font-serif-display text-2xl mt-2">Your application has been received.</h3>
                                <p className="text-sm text-muted-foreground mt-3">You will hear back within 1–2 business days.</p>
                            </div>
                        ) : (
                            <form data-testid="workshops-application-form" onSubmit={submit} className="space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Field label="Full name">
                                        <Input data-testid="workshops-field-name" value={form.full_name} onChange={onChange("full_name")} required />
                                    </Field>
                                    <Field label="Email">
                                        <Input data-testid="workshops-field-email" type="email" value={form.email} onChange={onChange("email")} required />
                                    </Field>
                                    <Field label="Phone">
                                        <Input data-testid="workshops-field-phone" value={form.phone} onChange={onChange("phone")} />
                                    </Field>
                                    <Field label="Offer">
                                        <Select value={form.offer_type} onValueChange={(v) => setForm({ ...form, offer_type: v })}>
                                            <SelectTrigger data-testid="workshops-field-offer"><SelectValue placeholder="Choose one" /></SelectTrigger>
                                            <SelectContent>
                                                {MENTORING_OFFERS.map((m) => (
                                                    <SelectItem key={m.id} value={m.title}>{m.title}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                </div>
                                <Field label="Where are you right now? (years working, focus, current portfolio)">
                                    <Textarea rows={3} value={form.current_status} onChange={onChange("current_status")} />
                                </Field>
                                <Field label="What would feel like a true win from working together?">
                                    <Textarea rows={3} value={form.goals} onChange={onChange("goals")} />
                                </Field>
                                <Field label="Portfolio or website (optional)">
                                    <Input value={form.portfolio_url} onChange={onChange("portfolio_url")} placeholder="https://" />
                                </Field>
                                <Field label="Why now?">
                                    <Textarea rows={3} value={form.why} onChange={onChange("why")} />
                                </Field>
                                <button
                                    data-testid="workshops-apply-button"
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-request"
                                >
                                    {submitting ? "Submitting…" : "Apply for Mentoring"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </Section>
        </div>
    );
}

const Field = ({ label, children }) => (
    <label className="block">
        <span className="kicker block mb-2">{label}</span>
        {children}
    </label>
);
